import React, { useCallback, useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { ORBIT_PANEL_OPEN_CLASS } from 'components/BottomBar';
import useOverwatchAccess from 'containers/Overwatch/permissions/useOverwatchAccess';

import { useOrbitContext } from './OrbitContext';
import OrbitProvider from './OrbitProvider';
import OrbitThemeScope from './OrbitThemeScope';
import OrbitBodyErrorBoundary from './components/OrbitBodyErrorBoundary';
import OrbitComposer from './components/OrbitComposer';
import OrbitConflictCard from './components/OrbitConflictCard';
import OrbitContextBlock from './components/OrbitContextBlock';
import OrbitConversationList from './components/OrbitConversationList';
import OrbitHomeState from './components/OrbitHomeState';
import OrbitIdleState from './components/OrbitIdleState';
import OrbitLauncher from './components/OrbitLauncher';
import OrbitMessageList from './components/OrbitMessageList';
import OrbitSidebar from './components/OrbitSidebar';
import { ORBIT_CONTEXT_DETECTION_ENABLED, ORBIT_PAGE_ROUTE } from './constants';
import OrbitL4ActionProvider from './l4/OrbitL4ActionProvider';
import { getOrbitScreenLabelKey } from './screenLabels';
import { orbitTokens } from './styles';
import useOrbitAccess from './useOrbitAccess';

export interface IOrbitShellProps {
  children?: React.ReactNode;
  /**
   * The app's nav sider, rendered inside OrbitProvider so a nav row can open the panel. It sits in
   * the same flex row as the content and the panel, exactly where IndexPage used to render it.
   * PLA-8844.
   */
  sider?: React.ReactNode;
  /**
   * `true` when Orbit takes the screen (the panel opens, or the full page becomes the route) and
   * `false` when it hands it back, so the app can fold its nav sider out of the way meanwhile.
   * Must be stable: a new function per render reads as Orbit closing and reopening.
   */
  onActiveChange?: (active: boolean) => void;
}

// Mirrors the Sider's sticky 100vh shell (App/pages/index.tsx) so the panel stays put while the
// content scrolls under it. z-index matches the Sider's for the same forms' bottom-bar reason.
// TODO(AF-10): no frame draws the shell, so the curve and duration below are invented.
const OrbitSidebarWrapper = styled.div<{ open: boolean }>`
  flex: 0 0 auto;
  height: 100vh;
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 20;
  width: ${({ open }) => (open ? orbitTokens.panelWidth : '0')};
  /*
    The panel holds its full width inside a clipped, panel-coloured wrapper, so opening reveals it
    instead of reflowing it. This replaces two faults: with no background, the animating wrapper
    showed the page behind it as a white band, and a percentage translateX animated next to the
    width resolved against a width that was itself animating, shoving the panel sideways by its
    own growing width before it snapped into place.
  */
  overflow: hidden;
  background: ${orbitTokens.surface};
  transition: width 0.24s cubic-bezier(0.32, 0.72, 0, 1);

  > * {
    width: ${orbitTokens.panelWidth};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const OVERWATCH_ROUTE = '/p/overwatch/*';

const OrbitShellLayout: React.FC<IOrbitShellProps> = ({ children, sider, onActiveChange }) => {
  const {
    open,
    closeSidebar,
    turns,
    activeTurnId,
    pendingQuestion,
    turnsLoading,
    sessionId,
    contextConflict,
    entryContext,
    newChat
  } = useOrbitContext();
  const access = useOrbitAccess();
  const { pathname } = useLocation();
  const { canUseOrbit: overwatchOrbit } = useOverwatchAccess();
  const [conversationsOpen, setConversationsOpen] = useState(false);
  /**
   * Holds the panel on its FIRST screen. New Chat has already dropped the session, so this only
   * decides which empty panel is drawn: the home one, which lists Recent Sessions, over the idle
   * one a recognised screen otherwise opens on.
   */
  const [landingOverride, setLandingOverride] = useState(false);
  const hasConversation = turns.length > 0 || activeTurnId !== null || pendingQuestion !== null;
  const showEmptyState = !hasConversation && !turnsLoading;
  const showLanding = showEmptyState || landingOverride;
  // Perx Overwatch asks for its own `use_perx_orbit_for_layer_6` grant on top of everything Orbit needs elsewhere,
  // so on its routes the launcher and the panel go together, and an open panel closes. PLA-9674.
  const onFullPage = matchPath(ORBIT_PAGE_ROUTE, pathname) !== null;
  const allowed =
    access === 'allowed' &&
    !onFullPage &&
    (!matchPath(OVERWATCH_ROUTE, pathname) || overwatchOrbit);
  // The full page counts while access is still resolving, so a reload of it never draws the nav
  // open first and folds it a beat later.
  const active = (allowed && open) || (onFullPage && access !== 'denied');
  /**
   * Which empty state to draw. The frames give no rule — both the home and the idle panel are drawn
   * over Customer Insights — so this is a decision, not a reading: the idle panel is entirely a
   * statement about a detected context, and a resolved screen label is the only context signal the
   * dashboard actually has. The same condition puts the context block on screen above it.
   */
  const hasContext =
    ORBIT_CONTEXT_DETECTION_ENABLED && getOrbitScreenLabelKey(entryContext.screen) !== undefined;

  useEffect(() => {
    if (!open) {
      setConversationsOpen(false);
      // Reopening lands on the live conversation, not on the screen the user last backed out to:
      // the panel is reopened to carry on, and the transcript is the thing that was left running.
      setLandingOverride(false);
    }
  }, [open]);

  /**
   * A conversation to show outranks the override. Asking from the first screen is the case that
   * matters: `ask()` sets `pendingQuestion` before its POST, so the transcript is on screen with
   * the question on it by the time the answer arrives. Resuming one from the list is the same rule.
   * The guard is what keeps New Chat's own landing: dropping the session empties all three
   * dependencies, and an unguarded reset would hand a recognised screen back its idle panel.
   */
  useEffect(() => {
    if (hasConversation) {
      setLandingOverride(false);
    }
  }, [sessionId, pendingQuestion, hasConversation]);

  // An access flip mid-session (an in-place tenant switch, a permission refresh) has to reach an
  // ALREADY OPEN panel, not just the launcher: otherwise the sidebar, the composer and the 5s poll
  // all keep running for a tenant that is no longer entitled to them.
  useEffect(() => {
    if (!allowed && open) {
      closeSidebar();
    }
  }, [allowed, open, closeSidebar]);

  // The cleanup reports the hand-back on unmount too, so a crash that drops the layout to the error
  // boundary's fallback still gives the nav back.
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    onActiveChange?.(true);

    return () => onActiveChange?.(false);
  }, [active, onActiveChange]);

  // The step forms' fixed bottom bar is not in the flex row the panel shrinks, so it has to be told
  // to keep its buttons clear of the panel itself; see ORBIT_PANEL_OPEN_CLASS.
  useEffect(() => {
    document.body.classList.toggle(ORBIT_PANEL_OPEN_CLASS, allowed && open);

    return () => {
      document.body.classList.remove(ORBIT_PANEL_OPEN_CLASS);
    };
  }, [allowed, open]);

  const toggleConversations = useCallback(() => setConversationsOpen(prev => !prev), []);
  const closeConversations = useCallback(() => setConversationsOpen(false), []);

  /**
   * The header's New Chat (`5118:16681`) is also the panel's way home (user, 2026-09-14): it leaves
   * the conversation list, which lists the sessions the new one replaces, and lands on the first
   * screen rather than on an empty transcript.
   */
  const startNewChat = useCallback(() => {
    setConversationsOpen(false);
    newChat();
    setLandingOverride(true);
  }, [newChat]);

  let emptyState: React.ReactNode = null;

  if (showLanding) {
    /*
      New Chat lands on the HOME panel, not the idle one. The idle panel is what a screen with a
      detected context OPENS on; New Chat is a deliberate step away from the running investigation,
      and the home panel is the only one that lists Recent Sessions — scoping it to unrecognised
      routes left that list unreachable from both Orbit-enabled screens (PLA-8844 D6).
    */
    emptyState =
      hasContext && !landingOverride ? (
        <OrbitIdleState onViewHistory={toggleConversations} />
      ) : (
        <OrbitHomeState onViewHistory={toggleConversations} />
      );
  }

  // `children` stays a DIRECT child of the app's has-sider `Layout`, so the antd
  // `.ant-layout-has-sider > .ant-layout { width: 0 }` rule keeps shrinking the content beside the
  // panel. An intervening flex wrapper would drop that rule and force a hand-rolled substitute onto
  // every tenant, flagged or not — the panel is simply a third flex child of the same row instead.
  return (
    <>
      {sider}
      {children}
      {allowed ? (
        <OrbitThemeScope>
          <OrbitSidebarWrapper open={open} data-testid="orbit-sidebar-wrapper">
            <OrbitSidebar
              footer={conversationsOpen ? undefined : <OrbitComposer />}
              historyOpen={conversationsOpen}
              /*
                Inert under the conflict card, which is a decision about the running investigation
                and has to be answered before the header changes screens underneath it. It covers
                History as well as New Chat: both change which body the panel draws.
              */
              newChatDisabled={contextConflict}
              onNewChat={startNewChat}
              /*
                The header's History glyph opens and closes the same conversation list the first
                screen's "View all history" link opens, so both go through `toggleConversations`.
              */
              onHistory={toggleConversations}
              /*
                The card interrupts the panel rather than queueing at the end of the transcript,
                where it scrolled out of view and could be missed (`5032:7990` draws it over a
                scrim, floating). It is withheld over the conversation list: the decision is about
                the running investigation, and the list is a different body altogether.
              */
              overlay={
                ORBIT_CONTEXT_DETECTION_ENABLED && contextConflict && !conversationsOpen ? (
                  <OrbitConflictCard />
                ) : null
              }
            >
              {/*
                Keyed on the body variant: @sentry/react's boundary clears its error only through
                `resetError`, so without a remount the retry card would stay on screen after the
                user switched the body to the conversation list and back.
              */}
              <OrbitBodyErrorBoundary key={conversationsOpen ? 'conversations' : 'thread'}>
                {conversationsOpen ? (
                  <OrbitConversationList onClose={closeConversations} />
                ) : (
                  <>
                    {/*
                      Pinned over the transcript as well as the empty panel (user, 2026-09-14):
                      `5032:10863` and `5017:9854` draw the chip row as the body's first child above
                      a running thread, and the user asked for one section that stays there while
                      the thread scrolls under it.
                    */}
                    {ORBIT_CONTEXT_DETECTION_ENABLED ? <OrbitContextBlock /> : null}
                    {showLanding ? null : <OrbitMessageList />}
                    {emptyState}
                  </>
                )}
              </OrbitBodyErrorBoundary>
            </OrbitSidebar>
          </OrbitSidebarWrapper>
          <OrbitLauncher />
        </OrbitThemeScope>
      ) : null}
    </>
  );
};

/**
 * Shell-level mount for Orbit: renders the sidebar as a sibling of the routed content inside the
 * app's has-sider row, so opening the panel shrinks the page instead of overlaying it. Mounted once
 * in `IndexPage`, so the provider (session, polling) survives route changes.
 *
 * The whole Orbit tree — provider included — sits OUTSIDE the app's `Sentry.ErrorBoundary`, which
 * `IndexPage` opens further down inside `Layout.Content`. The boundary below is what stands in for
 * it: its fallback is the page content on its own, so an Orbit crash costs the assistant and
 * nothing else, and is still reported to Sentry.
 */
const OrbitShell: React.FC<IOrbitShellProps> = ({ children, sider, onActiveChange }) => (
  <OrbitBodyErrorBoundary
    fallback={
      <>
        {sider}
        {children}
      </>
    }
  >
    <OrbitProvider>
      <OrbitL4ActionProvider>
        <OrbitShellLayout sider={sider} onActiveChange={onActiveChange}>
          {children}
        </OrbitShellLayout>
      </OrbitL4ActionProvider>
    </OrbitProvider>
  </OrbitBodyErrorBoundary>
);

export default OrbitShell;
